import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Loader2, Save, Printer, PlayCircle, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAssessment } from "@/context/AssessmentContext";
import { useAuth } from "@/context/AuthContext";
import { getExerciseVideo, getYouTubeSearchUrl, getExerciseThumbnail } from "@/lib/exerciseVideos";

const PHASES = [
  { key: "inhibit", label: "Loosen", desc: "Roll out and release the tight muscles first", color: "#E07A5F" },
  { key: "lengthen", label: "Stretch", desc: "Slowly stretch the muscles that got short", color: "#F2CC8F" },
  { key: "activate", label: "Wake Up", desc: "Turn on the weak muscles with easy targeted moves", color: "#52796F" },
  { key: "integrate", label: "Move Well", desc: "Put it all together in real everyday movements", color: "#1B4332" },
];

export default function Report() {
  const { data, update } = useAssessment();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(true);
  const [report, setReport] = useState(data.report);
  const [demo, setDemo] = useState(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (report) { setBusy(false); return; }
    if (!user) { navigate("/login", { state: { from: "/report" } }); return; }
    (async () => {
      try {
        const { data: r } = await http.post("/assessment/generate-report", {
          safety: data.safety, vision: data.vision, pattern: data.pattern, muscular: data.muscular,
        });
        setReport(r);
        update("report", r);
      } catch (e) {
        if (e.response?.status === 402) {
          setLocked(true);
        } else {
          toast.error("Could not generate report");
        }
      } finally { setBusy(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    try {
      const { data: r } = await http.post("/assessment/save", {
        safety: data.safety, vision: data.vision, pattern: data.pattern, muscular: data.muscular, report,
      });
      if (r.skipped) toast.info("Admin data isn't stored");
      else toast.success("Saved to your history");
    } catch (e) {
      if (e.response?.status === 402) navigate("/pricing");
      else toast.error("Save failed");
    }
  };

  if (busy) {
    return (
      <div className="text-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-forest mx-auto mb-4" />
        <p className="font-heading text-lg text-forest">Making your plan…</p>
      </div>
    );
  }

  // Paywall
  if (locked || (!report && !user?.has_access && user?.role !== "admin")) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-honey/30 flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-forest" />
        </div>
        <h1 className="font-heading text-3xl font-semibold text-forest mb-3">Your plan is ready</h1>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          Unlock your personalized 4-step exercise plan, video demos, chat, and progress tracking for just <span className="font-medium text-forest">₹199</span> — full access for 3 months.
        </p>
        <Card className="p-6 bg-white border-forest/10 mb-6 text-left">
          <p className="text-xs uppercase tracking-[0.2em] text-sage mb-3">What you're unlocking</p>
          <ul className="text-sm space-y-2">
            <li className="flex gap-2"><Sparkles className="w-4 h-4 text-forest shrink-0 mt-0.5" /> Your custom 4-phase plan (Loosen · Stretch · Wake Up · Move Well)</li>
            <li className="flex gap-2"><Sparkles className="w-4 h-4 text-forest shrink-0 mt-0.5" /> Video demos for every exercise</li>
            <li className="flex gap-2"><Sparkles className="w-4 h-4 text-forest shrink-0 mt-0.5" /> AI physio chat, unlimited</li>
            <li className="flex gap-2"><Sparkles className="w-4 h-4 text-forest shrink-0 mt-0.5" /> Save history + progress chart</li>
          </ul>
        </Card>
        <Button onClick={() => navigate("/pricing")} size="lg" className="bg-forest hover:bg-forest/90 text-bone rounded-full px-8" data-testid="unlock-btn">
          Unlock for ₹199
        </Button>
      </div>
    );
  }

  const score = report?.posture_score ?? data.vision?.posture_score ?? 70;
  const grade = report?.severity_grade ?? data.vision?.severity_grade ?? "Grade 2 - Moderate";

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-2 mb-4 text-sm text-forest font-medium">
        <FileText className="w-4 h-4" /> Your Plan
      </div>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-forest mb-2">Here's what's happening & what to do</h1>
          <p className="text-muted-foreground max-w-2xl">{report?.summary || "Personalized plan based on your check-in."}</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" onClick={() => window.print()} data-testid="print-btn"><Printer className="w-4 h-4 mr-2" /> Print</Button>
          <Button onClick={save} className="bg-forest hover:bg-forest/90 text-bone" data-testid="save-report-btn">
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        <Card className="lg:col-span-4 p-6 bg-forest text-bone border-forest">
          <p className="text-xs uppercase tracking-[0.2em] opacity-70 mb-2">Posture score</p>
          <p className="font-heading text-6xl font-bold mb-2">{score}</p>
          <p className="text-sm opacity-90 mb-4">out of 100</p>
          <Progress value={score} className="h-2 bg-bone/20" />
          <Badge className="mt-4 bg-honey text-forest border-0">{grade}</Badge>
        </Card>

        <Card className="lg:col-span-8 p-6 bg-white border-forest/10">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Focus areas</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {(Array.isArray(report?.priority_regions) ? report.priority_regions : [data.pattern?.plain_name || data.pattern?.name]).filter(Boolean).map((r, i) => (
              <Badge key={i} variant="outline" className="border-forest/30 text-forest">{r}</Badge>
            ))}
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 mt-4">When to see a real physio</p>
          <ul className="text-sm space-y-1">
            {(Array.isArray(report?.when_to_see_pt) ? report.when_to_see_pt : ["Symptoms not improving after 2 weeks", "Pain gets worse with these exercises"]).map((s, i) => (
              <li key={i}>• {s}</li>
            ))}
          </ul>
        </Card>
      </div>

      <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-forest mb-4">Your 4-step plan</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {PHASES.map((p, idx) => {
          const items = report?.phases?.[p.key] || [];
          return (
            <Card key={p.key} className="p-5 bg-white border-forest/10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-md flex items-center justify-center font-heading font-semibold text-bone text-sm" style={{ background: p.color }}>
                  {idx + 1}
                </div>
                <div>
                  <p className="font-heading font-semibold text-forest">{p.label}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">{p.desc}</p>
                </div>
              </div>
              <ul className="space-y-3">
                {items.length === 0 ? (
                  <li className="text-xs text-muted-foreground">No exercises assigned.</li>
                ) : items.map((ex, i) => {
                  const thumb = getExerciseThumbnail(ex.name);
                  const openDemo = () => {
                    const v = getExerciseVideo(ex.name);
                    setDemo({ name: ex.name, url: v.url, cue: ex.cue });
                  };
                  return (
                  <li key={i} className="text-sm border-l-2 pl-2" style={{ borderColor: p.color }}>
                    <div className="flex items-start gap-3">
                      {thumb && (
                        <button
                          onClick={openDemo}
                          className="relative shrink-0 w-24 h-14 rounded overflow-hidden bg-forest/5 group focus:outline-none focus:ring-2 focus:ring-sage"
                          title={`Preview ${ex.name}`}
                          data-testid={`demo-thumb-${p.key}-${i}`}
                        >
                          <img
                            src={thumb}
                            alt={`${ex.name} preview`}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/10 transition-colors">
                            <PlayCircle className="w-6 h-6 text-white drop-shadow" strokeWidth={1.75} />
                          </span>
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium leading-tight">{ex.name}</p>
                          <button
                            onClick={openDemo}
                            className="text-forest hover:text-sage shrink-0"
                            title="Watch demo"
                            data-testid={`demo-btn-${p.key}-${i}`}
                          >
                            <PlayCircle className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">{ex.target}</p>
                        <p className="text-xs">{ex.duration || ex.sets_reps}</p>
                        {ex.cue && <p className="text-[11px] italic text-sage mt-1">→ {ex.cue}</p>}
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>
            </Card>
          );
        })}
      </div>

      {Array.isArray(report?.ergonomics) && report.ergonomics.length > 0 && (
        <Card className="p-6 bg-sage/8 border-sage/20 mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-sage mb-3">Everyday habits to fix</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {report.ergonomics.map((e, i) => <li key={i}>• {e}</li>)}
          </ul>
        </Card>
      )}

      <div className="text-[11px] text-muted-foreground text-center py-4 border-t border-forest/10">
        Exercise videos are third-party content from YouTube. Stop immediately if you feel sharp pain.
        Apexture is educational content — not medical advice. See our <Link to="/disclaimer" className="underline">Disclaimer</Link>.
      </div>

      <Dialog open={!!demo} onOpenChange={(o) => !o && setDemo(null)}>
        <DialogContent className="max-w-3xl bg-white" data-testid="demo-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">{demo?.name}</DialogTitle>
          </DialogHeader>
          {demo && (
            <>
              <div className="aspect-video w-full rounded-lg overflow-hidden border border-forest/10 bg-black">
                <iframe
                  title={demo.name}
                  src={demo.url}
                  className="w-full h-full"
                  frameBorder="0"
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <a
                  href={getYouTubeSearchUrl(demo.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-forest underline hover:text-sage"
                  data-testid="youtube-search-link"
                >
                  Search more demos for "{demo.name}" on YouTube →
                </a>
              </div>
              {demo.cue && (
                <div className="mt-3 p-3 rounded bg-honey/15 border border-honey/40 text-sm">
                  <span className="font-medium text-forest">Form cue: </span>{demo.cue}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Third-party YouTube content. Stop if you feel sharp pain. See <Link to="/disclaimer" className="underline">Medical Disclaimer</Link>.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
